import { computed, onBeforeUnmount, Ref, ref, watch } from 'vue';

export interface UseStickyStackOffsetsOptions {
  /** Top offset of the first sticky element (e.g. main nav height). Default 58. */
  baseTop?: number;
}

/**
 * Computes dynamic `top` values for a stack of sticky elements so each sticks
 * directly below the previous one when the anchor (e.g. header) height changes.
 *
 * Use when you have:
 * - An anchor sticky element (e.g. header-actions-container) with variable height
 * - One or more sticky elements below it that should stick right under it
 *
 * @param headerRef - Ref to the top sticky element (anchor) whose height drives the stack
 * @param filterContainerRef - Optional ref to the second sticky element (used to compute table header top)
 * @param options - baseTop (default 58), enabled
 * @returns filterContainerTop (px), tableHeaderTop (px), and refs for binding in template
 */
export function useStickyStackOffsets(
  headerRef: Ref<HTMLElement | null>,
  filterContainerRef: Ref<HTMLElement | null> | undefined,
  options: UseStickyStackOffsetsOptions = {}
) {
  const { baseTop = 58 } = options;

  const headerHeight = ref(0);
  const filterContainerHeight = ref(0);

  const filterContainerTop = computed(() => baseTop + headerHeight.value);

  const tableHeaderTop = computed(() => {
    const filterTop = filterContainerTop.value;
    return filterTop + filterContainerHeight.value - 1;
  });

  let headerObserver: ResizeObserver | null = null;
  let filterObserver: ResizeObserver | null = null;

  function observeHeader(el: HTMLElement | null) {
    if (headerObserver) {
      headerObserver.disconnect();
      headerObserver = null;
    }
    if (!el) {
      headerHeight.value = 0;
      return;
    }
    const updateHeight = () => {
      headerHeight.value = el.getBoundingClientRect().height;
    };
    updateHeight();
    headerObserver = new ResizeObserver(updateHeight);
    headerObserver.observe(el);
  }

  function observeFilter(el: HTMLElement | null) {
    if (filterObserver) {
      filterObserver.disconnect();
      filterObserver = null;
    }
    if (!el) {
      filterContainerHeight.value = 0;
      return;
    }
    const updateHeight = () => {
      filterContainerHeight.value = el.getBoundingClientRect().height;
    };
    updateHeight();
    filterObserver = new ResizeObserver(updateHeight);
    filterObserver.observe(el);
  }

  watch(
    headerRef,
    el => {
      observeHeader(el);
    },
    { immediate: true }
  );

  watch(
    () => filterContainerRef?.value ?? null,
    el => {
      observeFilter(el ?? null);
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    headerObserver?.disconnect();
    filterObserver?.disconnect();
  });

  return {
    filterContainerTop,
    tableHeaderTop,
    headerHeight,
    filterContainerHeight,
  };
}
